import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

interface OnboardingContextType {
  isFirstTimeUser: boolean;
  isTourOpen: boolean;
  startTour: () => void;
  closeTour: () => void;
  completeTour: () => void;
}

// Service interface for dependency injection
interface OnboardingService {
  checkOnboardingStatus: () => Promise<{ hasCompletedOnboarding?: boolean }>;
  completeOnboarding: () => Promise<void>;
}

// Service injection for testability
let onboardingService: OnboardingService | null = null;

// Function to set the service (for testing)
export const setOnboardingService = (service: OnboardingService) => {
  onboardingService = service;
};

// Lazy load the service to avoid import issues in tests
const getOnboardingService = async (): Promise<OnboardingService> => {
  if (onboardingService) return onboardingService;
  
  // Only import in non-test environment
  if (process.env.NODE_ENV !== 'test') {
    try {
      const axios = (await import('axios')).default;
      return {
        checkOnboardingStatus: async () => {
          try {
            const response = await axios.get('/api/auth/user');
            return response.data as { hasCompletedOnboarding?: boolean };
          } catch (error) {
            console.error('Error checking onboarding status:', error);
            return { hasCompletedOnboarding: true }; // Default to completed to avoid showing tour on error
          }
        },
        completeOnboarding: async () => {
          try {
            await axios.post('/api/auth/complete-onboarding');
          } catch (error) {
            console.error('Error completing onboarding:', error);
          }
        }
      };
    } catch (error) {
      console.error('Error loading onboarding service:', error);
    }
  }
  
  // Fallback mock service for tests
  return {
    checkOnboardingStatus: async () => ({ hasCompletedOnboarding: true }),
    completeOnboarding: async () => {}
  };
};

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

export const OnboardingProvider: React.FC<{ 
  children: React.ReactNode; 
  disableAutoCheck?: boolean;
}> = ({ children, disableAutoCheck = false }) => {
  const [isFirstTimeUser, setIsFirstTimeUser] = useState<boolean>(false);
  const [isTourOpen, setIsTourOpen] = useState<boolean>(false);

  // Check if user has completed onboarding
  useEffect(() => {
    if (disableAutoCheck) return;
    
    const checkOnboardingStatus = async () => {
      try {
        const service = await getOnboardingService();
        const user = await service.checkOnboardingStatus();
        
        // If user hasn't completed onboarding and has data (budgets/transactions), show tour
        if (!user.hasCompletedOnboarding) {
          setIsFirstTimeUser(true);
          // Auto-start tour after a short delay for better UX
          setTimeout(() => {
            setIsTourOpen(true);
          }, 1000);
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        // Default to not showing tour on error
        setIsFirstTimeUser(false);
        setIsTourOpen(false);
      }
    };

    checkOnboardingStatus();
  }, [disableAutoCheck]);

  const startTour = useCallback(() => {
    setIsTourOpen(true);
  }, []);

  const closeTour = useCallback(() => {
    setIsTourOpen(false);
  }, []);

  const completeTour = useCallback(async () => {
    try {
      const service = await getOnboardingService();
      await service.completeOnboarding();
      setIsFirstTimeUser(false);
      setIsTourOpen(false);
    } catch (error) {
      console.error('Error completing onboarding:', error);
      // Still close the tour even if the API call fails
      setIsFirstTimeUser(false);
      setIsTourOpen(false);
    }
  }, []);

  const value = {
    isFirstTimeUser,
    isTourOpen,
    startTour,
    closeTour,
    completeTour
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}; 